import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Table from "../../../core/common/dataTable/index";
import { useGetInventoriesQuery } from "../api/starterKitApi";
import { useSelector } from "react-redux";
import { selectUserRole, selectCurrentUser } from "../../../core/data/slice/authSlice";
import TransferStockModal from "../components/TransferStockModal";
import ReturnStockModal from "../components/ReturnStockModal";
import { useCenter } from "../../peoples/centers/hooks/useCenter";
import CommonSelect from "../../../core/common/commonSelect";
import CountUp from "react-countup";
import { useCourse } from "../../configuration/course/hooks/useCourse";

const StarterKitInventoryList = () => {
  const routes = all_routes;
  const userRole = useSelector(selectUserRole);
  const currentUser = useSelector(selectCurrentUser);
  const [page, setPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [selectedInventory, setSelectedInventory] = useState<any>(null);
  const [filters, setFilters] = useState<{ center_id?: number; course_id?: number }>({});
  const [tempFilters, setTempFilters] = useState<{ center_id?: number; course_id?: number }>({});
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);

  const allCentersResult = useCenter({ skip: true });
  const { data: centerResponse } = allCentersResult.useCenterList();
  const centers = centerResponse?.data?.items || [];

  const centerOptions = centers.map((center) => ({
    value: center.id.toString(),
    label: center.name,
  }));

  const { getCourseState } = useCourse();
  const { courses } = getCourseState();
  const courseOptions = courses.map(course => ({
    value: course.id.toString(),
    label: course.name,
  }));

  const { data: mainCenterData } = useGetInventoriesQuery({
    is_main_center_stock: 1,
    course_id: filters.course_id
  });
  const mainCenterStocks = mainCenterData?.data?.items || [];
  const totalAvailable = mainCenterStocks.reduce((sum: number, item: any) => sum + (item.available_stock || 0), 0);
  const totalStock = mainCenterStocks.reduce((sum: number, item: any) => sum + (item.total_stock || 0), 0);

  const { data: allInventoriesData, isLoading: isInventoriesLoading, isError: inventoriesError } = useGetInventoriesQuery({
    size: 1000,
    is_main_center_stock: 0,
  });
  const allInventories = allInventoriesData?.data?.items || [];

  const { data: allMainCenterData } = useGetInventoriesQuery({
    is_main_center_stock: 1,
  });
  const allMainCenterStocks = allMainCenterData?.data?.items || [];
  const hasAllMainCenterStock = courses.length > 0 && allMainCenterStocks.length >= courses.length;

  const targetCenters = filters.center_id
    ? centers.filter((c: any) => c.id === filters.center_id)
    : (userRole === "superadmin" ? centers : centers.filter((c: any) => c.id === currentUser?.center_id));

  const searchedCenters = search
    ? targetCenters.filter((c: any) => c.name.toLowerCase().includes(search.toLowerCase()))
    : targetCenters;

  const inventoryTotalItems = searchedCenters.length;
  const displayCenters = searchedCenters.slice((page - 1) * recordsPerPage, page * recordsPerPage);

  const dynamicColumns = courses.map((course: any) => ({
    title: course.name,
    render: (record: any) => {
      const inv = allInventories.find((i: any) =>
        (Number(i.center_id) === record.id || Number(i.center?.id) === record.id) &&
        (Number(i.course_id) === course.id || Number(i.course?.id) === course.id)
      );
      return (
        <div>
          <span className="text-dark fw-bold">{inv?.available_stock ?? 0}</span>
          <span className="text-muted ms-1">/ {inv?.total_stock ?? 0}</span>
        </div>
      );
    }
  }));

  const handlePageChange = (p: number, pageSize?: number) => {
    setPage(p);
    if (pageSize) {
      setRecordsPerPage(pageSize);
    }
  };

  const handleSearchChange = (text: string) => {
    setSearch(text);
    setPage(1);
  };

  const handleFilterChange = (filterName: string, value: any) => {
    setTempFilters(prev => ({
      ...prev,
      [filterName]: value?.value ? Number(value.value) : undefined
    }));
  };

  const handleApplyFilters = () => {
    setFilters(tempFilters);
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({});
    setTempFilters({});
    setPage(1);
  };

  const columns: any[] = [
    {
      title: "S. No",
      render: (_: any, __: any, index: number) => index + 1 + (page - 1) * recordsPerPage,
    },
    {
      title: "Center",
      render: (record: any) => <span className="fw-medium">{record?.name || "N/A"}</span>,
    },
    ...dynamicColumns,
  ];

  if (userRole === "superadmin") {
    columns.push({
      title: "Action",
      render: (_: any, record: any) => {
        const centerInventories = allInventories.filter((i: any) =>
          Number(i.center_id) === record?.id || Number(i.center?.id) === record?.id
        );
        const totalAvailableStock = centerInventories.reduce((sum, i) => sum + (i.available_stock || 0), 0);

        return (
          <div className="d-flex align-items-center">
            <div className="dropdown">
              <Link
                to="#"
                className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="ti ti-dots-vertical fs-14" />
              </Link>
              <ul className="dropdown-menu dropdown-menu-right p-3">
                <li>
                  <Link
                    className="dropdown-item rounded-1"
                    to="#"
                    data-bs-toggle="modal"
                    data-bs-target="#transfer_stock"
                    onClick={() => setSelectedInventory({
                      center_id: record.id?.toString(),
                      center_name: record.name,
                      available_stock: 0
                    })}
                  >
                    <i className="ti ti-exchange me-2" />
                    Assign to Center
                  </Link>
                </li>
                {totalAvailableStock > 0 && (
                  <li>
                    <Link
                      className="dropdown-item rounded-1"
                      to="#"
                      data-bs-toggle="modal"
                      data-bs-target="#return_stock_modal"
                      onClick={() => setSelectedInventory({
                        center_id: record.id?.toString(),
                        center_name: record.name,
                        available_stock: 0
                      })}
                    >
                      <i className="ti ti-arrow-back-up me-2" />
                      Return from Center
                    </Link>
                  </li>
                )}
              </ul>
            </div>
          </div>
        );
      }
    });
  }

  const findOptionByValue = (options: any[], value: string | number | undefined) => {
    if (!value) return undefined;
    return options.find((option) => option.value === value.toString());
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Starter Kit Inventory</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">Inventory</li>
                <li className="breadcrumb-item active" aria-current="page">
                  Stock List
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
          </div>
        </div>

        {userRole === "superadmin" && (
          <div className="row">
            <div className="col-xxl-4 col-md-4 col-sm-12 d-flex">
              <div className="card flex-fill animate-card border-0">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-xl me-2 bg-primary-transparent p-1">
                      <i className="ti ti-package fs-24 text-primary"></i>
                    </div>
                    <div className="overflow-hidden flex-fill">
                      <div className="d-flex align-items-center justify-content-between">
                        <h2>
                          <CountUp end={totalStock} />
                        </h2>
                      </div>
                      <p>Overall Total Stock</p>
                    </div>
                  </div>
                  <div className="d-flex justify-content-between mt-2 text-muted fs-13">
                    <span>Available: {totalAvailable}</span>
                    <span>Distributed: {totalStock - totalAvailable}</span>
                  </div>
                </div>
              </div>
            </div>
            {courses.map((course: any) => {
              const stock = mainCenterStocks.find((s: any) =>
                Number(s.course_id) === Number(course.id) || Number(s.course?.id) === Number(course.id)
              );
              const courseTotal = stock?.total_stock || 0;
              const courseAvailable = stock?.available_stock || 0;
              const courseDistributed = courseTotal - courseAvailable;

              return (
                <div className="col-xxl-4 col-md-4 col-sm-12 d-flex" key={course.id}>
                  <div className="card flex-fill animate-card border-0">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="avatar avatar-xl me-2 bg-success-transparent p-1">
                          <i className="ti ti-books fs-24 text-success"></i>
                        </div>
                        <div className="overflow-hidden flex-fill">
                          <div className="d-flex align-items-center justify-content-between">
                            <h2>
                              <CountUp end={courseTotal} />
                            </h2>
                          </div>
                          <p>{course.name || 'Unknown Course'} Stock</p>
                        </div>
                      </div>
                      <div className="d-flex justify-content-between mt-2 text-muted fs-13">
                        <span>Available: {courseAvailable}</span>
                        <span>Distributed: {courseDistributed}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">Inventory List</h4>
            <div className="d-flex align-items-center flex-wrap">
              {userRole === "superadmin" && (
                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                    onClick={() => setTempFilters(filters)}
                  >
                    <i className="ti ti-filter me-2" />
                    Filter
                  </Link>
                  <div className="dropdown-menu drop-width" ref={dropdownMenuRef}>
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter</h4>
                      </div>
                      <div className="p-3 pb-0 border-bottom">
                        <div className="row">
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">Course</label>
                              <CommonSelect
                                className="select"
                                options={courseOptions}
                                value={findOptionByValue(courseOptions, tempFilters.course_id)}
                                onChange={(value) => handleFilterChange("course_id", value)}
                              />
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">Center</label>
                              <CommonSelect
                                className="select"
                                options={centerOptions}
                                value={findOptionByValue(centerOptions, tempFilters.center_id)}
                                onChange={(value) => handleFilterChange("center_id", value)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-end">
                        <button type="button" className="btn btn-light me-3" onClick={handleResetFilters}>
                          Reset
                        </button>
                        <button type="button" className="btn btn-primary" onClick={handleApplyFilters}>
                          Apply
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="card-body p-0 py-3">
            {isInventoriesLoading ? (
              <div className="text-center">Loading inventory...</div>
            ) : inventoriesError ? (
              <div className="alert alert-danger m-3">
                Error loading inventory.
              </div>
            ) : (
              <Table
                dataSource={displayCenters}
                columns={columns}
                Selection={false}
                onSearchChange={handleSearchChange}
                pagination={{
                  current: page,
                  pageSize: recordsPerPage,
                  total: inventoryTotalItems || 0,
                  onChange: handlePageChange,
                  pageSizeOptions: ["All", 10, 20, 30],
                }}
              />
            )}
          </div>
        </div>
      </div>
      {userRole === "superadmin" && (
        <>
          <TransferStockModal
            initialData={selectedInventory}
            onClose={() => setSelectedInventory(null)}
          />
          <ReturnStockModal
            initialData={selectedInventory}
            onClose={() => setSelectedInventory(null)}
          />
        </>
      )}
    </div>
  );
};

export default StarterKitInventoryList;
